import React, { useState } from "react";
import { todayISO } from "../../lib/dates";
import { toast } from "sonner";
import { Download, CheckCircle2 } from "lucide-react";
import api, { formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { downloadPdf } from "../../components/FileUpload";
import { PageHero, Section } from "../../components/Page";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const CATEGORIES = ["Full Member (Akuapem descent)", "Spouse / Partner of Akuapem descent", "Child of Akuapem descent (21+)", "Associate / Friend of Akuapem"];
const EMPTY = {
  first_name: "", last_name: "", dob: "", email: "", phone: "", address: "", address2: "", city: "", state: "MA", postal_code: "",
  occupation: "", from_ghana: "Yes", akuapem_town: "", country_of_origin: "", membership_category: CATEGORIES[0], interest: "",
  ec_first_name: "", ec_last_name: "", ec_relationship: "", ec_phone: "", agree: false, media_consent: false, signature: "",
  signed_date: todayISO(),
};

function Field({ label, k, form, set, type = "text", testId, required }) {
  const { t } = useLang();
  return (
    <div>
      <Label>{t(label)}{required && <span className="text-crimson"> *</span>}</Label>
      <Input type={type} value={form[k]} onChange={(e) => set({ ...form, [k]: e.target.value })} data-testid={testId || `reg-${k}`} />
    </div>
  );
}

export default function Registration() {
  const { user } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState(() => {
    if (!user) return EMPTY;
    const [first = "", ...rest] = (user.name || "").split(" ");
    return { ...EMPTY, first_name: first, last_name: rest.join(" "), email: user.email || "", phone: user.phone || "", address: user.address || "" };
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const required = ["first_name", "last_name", "dob", "email", "phone", "address", "city", "state", "postal_code", "ec_first_name", "ec_last_name", "ec_relationship", "ec_phone", "signature"];
  const valid = required.every((k) => String(form[k]).trim()) && form.agree;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/registration", form);
      setResult(data); toast.success("Registration submitted!");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="registration-page">
      <PageHero eyebrow="Ofie! Biakoye!" title="Membership Registration"
        subtitle="Join our family and tradition-oriented community. By submitting this form you become part of the newly constituted Okuapemman Association of Massachusetts." />
      <Section>
        {result ? (
          <div className="glass mx-auto max-w-2xl rounded-2xl p-8 text-center" data-testid="reg-success">
            <CheckCircle2 size={48} className="mx-auto text-palm" />
            <h2 className="mt-4 font-serif-display text-3xl font-bold text-crimson">{t("Medaase — Registration received!")}</h2>
            <p className="mt-2 text-muted-foreground">Reference <span className="font-mono-accent font-bold text-crimson" data-testid="reg-tracking">{result.tracking_id}</span>. A copy has been emailed to you{user ? " and saved to your member Documents" : ""}.</p>
            {result.pdf_url && user && (
              <Button className="mt-6 gap-2 bg-crimson" data-testid="reg-download-pdf" onClick={() => downloadPdf(result.pdf_url.replace(/^\/api/, ""), `registration-${result.tracking_id}.pdf`)}>
                <Download size={16} /> {t("Download my registration PDF")}
              </Button>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-8">
            <div className="glass rounded-2xl p-6 space-y-4">
              <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Personal details")}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name" k="first_name" form={form} set={setForm} required />
                <Field label="Last name" k="last_name" form={form} set={setForm} required />
                <Field label="Date of birth" k="dob" type="date" form={form} set={setForm} required />
                <Field label="Email address" k="email" type="email" form={form} set={setForm} required />
                <Field label="Phone number" k="phone" form={form} set={setForm} required />
                <Field label="Occupation" k="occupation" form={form} set={setForm} />
              </div>
              <h4 className="font-semibold text-palm">{t("Home address")}</h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Address" k="address" form={form} set={setForm} required />
                <Field label="Address line 2" k="address2" form={form} set={setForm} />
                <Field label="City" k="city" form={form} set={setForm} required />
                <Field label="State" k="state" form={form} set={setForm} required />
                <Field label="Postal code" k="postal_code" form={form} set={setForm} required />
              </div>
            </div>
            <div className="glass rounded-2xl p-6 space-y-4">
              <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Heritage & membership")}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>{t("Are you originally from Ghana?")}</Label>
                  <Select value={form.from_ghana} onValueChange={(v) => setForm({ ...form, from_ghana: v })}>
                    <SelectTrigger data-testid="reg-from_ghana"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                  </Select></div>
                {form.from_ghana === "Yes"
                  ? <Field label="Which part of Akuapem are you or your parents from?" k="akuapem_town" form={form} set={setForm} />
                  : <Field label="If no, what is your country of origin?" k="country_of_origin" form={form} set={setForm} />}
                <div className="sm:col-span-2"><Label>{t("Membership category")}</Label>
                  <Select value={form.membership_category} onValueChange={(v) => setForm({ ...form, membership_category: v })}>
                    <SelectTrigger data-testid="reg-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label>{t("Briefly describe your interest in joining")}</Label>
                <Textarea value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} data-testid="reg-interest" /></div>
            </div>
            <div className="glass rounded-2xl p-6 space-y-4">
              <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Emergency contact")}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name" k="ec_first_name" form={form} set={setForm} required />
                <Field label="Last name" k="ec_last_name" form={form} set={setForm} required />
                <Field label="Relationship to you" k="ec_relationship" form={form} set={setForm} required />
                <Field label="Phone number" k="ec_phone" form={form} set={setForm} required />
              </div>
            </div>
            <div className="glass rounded-2xl p-6 space-y-4">
              <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Pledge & signature")}</h3>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={form.agree} onCheckedChange={(v) => setForm({ ...form, agree: !!v })} data-testid="reg-agree" />
                <span>{t("I agree to abide by the constitution and by-laws of the Okuapemman Association of Massachusetts.")} <em className="text-crimson">Ofie! Biakoye!</em> <a href="/constitution" className="underline text-palm">{t("Read the constitution")}</a> · <a href="/terms" className="underline text-palm" data-testid="reg-terms-link">{t("Terms of Use")}</a> · <a href="/privacy" className="underline text-palm" data-testid="reg-privacy-link">{t("Privacy Policy")}</a></span>
              </label>
            <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>{t("Signature (type your full name)")} <span className="text-crimson">*</span></Label>
                  <Input value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} className="font-serif-display italic text-lg" data-testid="reg-signature" /></div>
                <Field label="Date" k="signed_date" type="date" form={form} set={setForm} required />
              </div>
            </div>
            <div className="glass rounded-2xl p-6 space-y-3" data-testid="reg-media-consent-section">
              <h3 className="font-serif-display text-2xl font-bold text-crimson">{t("Photo & video consent")}</h3>
              <p className="text-sm text-muted-foreground">{t("Optional. We sometimes share photos and videos from meetings and events. We will not publish images of you unless you say it is okay. You can change this any time in your member profile.")}</p>
              <label className="flex items-start gap-3 text-sm">
                <Checkbox checked={form.media_consent} onCheckedChange={(v) => setForm({ ...form, media_consent: !!v })} data-testid="reg-media-consent" />
                <span>{t("I consent to photographs and video recordings of me taken at association events and meetings being published on the association's website, member portal and social media channels.")}</span>
              </label>
            </div>
            <Button onClick={submit} disabled={!valid || busy} className="w-full bg-crimson hover:bg-crimson-hover" size="lg" data-testid="reg-submit">
              {busy ? t("Submitting…") : t("Submit registration")}
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
}
